<template>
  <div :id="containerId" class="container-stacked-bar">
    <SelectButton
      icon="timeline"
      :options="subjects"
      label="label"
      :valueId="selectedSubjectId"
      :showPosition="true"
      :autoWidth="true"
      :showTitle="false"
      :enableSearch="false"
      :selectedFirst="false"
      @input="handleSubjectSelect"
    />
    <div class="chart-container">
      <canvas :id="canvasId"></canvas>
    </div>
  </div>
</template>

<script>
import { formatters, canvasMaker, colorsDefault } from '@/_helpers'
import SelectButton from '@/components/inputs/SelectButton.vue'
import('chart.js').then((chartjs) => { window.Chart = chartjs.default })

export default {
  name: 'StackedBarImage',
  components: {
    SelectButton
  },
  props: {
    cardData: {
      type: Array,
      required: false
    },
    data: {
      type: Object,
      required: false
    },
    question: {
      type: Object,
      required: false
    }
  },
  data () {
    return {
      subjects: [],
      selectedSubjectId: '',
      chart: null,
      containerId: '',
      canvasId: ''
    }
  },
  computed: {
    lang () {
      return this.$store.getters['account/getLang']
    },
    selectedSubject () {
      return this.subjects.find(subject => subject._id === this.selectedSubjectId)
    }
  },
  created () {
    this.containerId = `container-${canvasMaker.makeCanvasIdOnlyNumbers()}`
    this.canvasId = `canvas-${canvasMaker.makeCanvasIdOnlyNumbers()}`
  },
  watch: {
    subjects: {
      immediate: true,
      handler (newSubjects) {
        if (newSubjects.length > 0 && !this.selectedSubjectId) {
          this.selectedSubjectId = newSubjects[0]._id
        }
      }
    },
    selectedSubjectId: {
      handler (newId) {
        if (newId) {
          this.$nextTick(this.createChart)
        }
      }
    }
  },
  mounted () {
    this.formatData()
  },
  methods: {
    formatData () {
      if (!this.data?.overtime || !this.cardData?.[0]?.questions) return

      const formatDate = this.getFormatDate()
      const questionData = this.cardData[0].questions.find(
        dataQuestion => dataQuestion.question && dataQuestion.question._id === this.question._id
      )

      if (!questionData) return

      const colors = this.getChartColors()
      const subjectIds = this.data.overtime[0].slice(2)
      const subjectsMap = new Map()
      const optionDataMap = new Map()

      this.processOvertimeData(subjectIds, subjectsMap, optionDataMap, formatDate)
      this.createSubjectsFromMaps(subjectsMap, optionDataMap, colors)

      this.$nextTick(this.createChart)
    },

    getFormatDate () {
      const isPeriodByDay = this.$route.name === 'today' ||
        (this.$route.query.q && ['today', 'yesterday'].includes(this.$route.query.q))
      return isPeriodByDay ? 'HH:mm' : 'MMM Do'
    },

    getChartColors () {
      if (this.cardData?.[0]?.colors) {
        return this.cardData[0].colors
      }
      const totalOptions = this.question.subjects[0]?.options?.length || 4
      return colorsDefault.getColorsDefault(totalOptions)
    },

    processOvertimeData (subjectIds, subjectsMap, optionDataMap, formatDate) {
      this.data.overtime.slice(1).forEach((timeItem) => {
        if (!timeItem || timeItem.length < 3 || !timeItem[0]) return
        const currentDate = formatters.formatDateByLangAndFixTimeZone(timeItem[0], this.lang, formatDate)

        for (let i = 2; i < timeItem.length; i++) {
          const data = timeItem[i]
          if (!data || !Array.isArray(data)) continue

          const subjectId = subjectIds[i - 2]
          if (!subjectId) continue

          this.initializeSubject(subjectId, subjectsMap, currentDate)
          this.processSubjectOptions(data, subjectId, currentDate, optionDataMap)
        }
      })
    },

    initializeSubject (subjectId, subjectsMap, currentDate) {
      if (!subjectsMap.has(subjectId)) {
        const subject = this.question.subjects.find(s => s._id === subjectId)
        if (subject) {
          subjectsMap.set(subjectId, {
            _id: subject._id,
            label: subject.label,
            options: subject.options,
            datasets: [],
            dates: []
          })
        }
      }

      const subject = subjectsMap.get(subjectId)
      if (subject && !subject.dates.includes(currentDate)) {
        subject.dates.push(currentDate)
      }
    },

    processSubjectOptions (data, subjectId, currentDate, optionDataMap) {
      data.forEach((option) => {
        if (Array.isArray(option) && option.length >= 3) {
          const [label, qty, optionId] = option
          const optionKey = `${subjectId}-${optionId}`

          if (!optionDataMap.has(optionKey)) {
            optionDataMap.set(optionKey, {
              label,
              optionId,
              data: new Map()
            })
          }

          optionDataMap.get(optionKey).data.set(currentDate, qty)
        }
      })
    },

    createSubjectsFromMaps (subjectsMap, optionDataMap, colors) {
      for (const [subjectId, subject] of subjectsMap) {
        subject.dates.sort()

        for (const [optionKey, optionData] of optionDataMap) {
          if (optionKey.startsWith(`${subjectId}-`)) {
            const matchingOption = subject.options.find(opt => opt._id === optionData.optionId)
            const dataPoints = subject.dates.map(date => optionData.data.get(date) || 0)

            subject.datasets.push({
              label: optionData.label,
              data: dataPoints,
              imageUrl: matchingOption ? matchingOption.label : ''
            })
          }
        }
      }

      this.subjects = Array.from(subjectsMap.values())
    },

    createChart () {
      if (!this.selectedSubject) return

      const canvas = document.getElementById(this.canvasId)
      if (!canvas) return

      if (this.chart) {
        this.chart.destroy()
      }

      const ctx = canvas.getContext('2d')
      const tooltipId = this.createTooltip()
      this.chart = new window.Chart(ctx, {
        type: 'bar',
        data: this.getChartData(),
        options: this.getChartOptions(tooltipId)
      })
    },

    createTooltip () {
      const tooltipId = `tooltip-${this.canvasId}`
      const existingTooltip = document.getElementById(tooltipId)
      if (existingTooltip) {
        existingTooltip.remove()
      }

      const newTooltip = document.createElement('div')
      newTooltip.id = tooltipId
      newTooltip.innerHTML = '<table></table>'
      document.body.appendChild(newTooltip)

      return tooltipId
    },

    getChartData () {
      const colors = this.getChartColors()
      return {
        labels: this.selectedSubject.dates,
        datasets: this.selectedSubject.datasets.map((dataset, index) => ({
          label: dataset.label,
          data: dataset.data,
          backgroundColor: colors[index % colors.length],
          borderWidth: 0,
          imageUrl: dataset.imageUrl,
          maxBarThickness: 50
        }))
      }
    },

    getChartOptions (tooltipId) {
      return {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            top: 10,
            right: 20,
            bottom: 10,
            left: 20
          }
        },
        scales: {
          xAxes: [{
            stacked: true,
            gridLines: {
              display: true,
              color: 'rgba(0, 0, 0, 0.1)',
              drawBorder: false
            },
            ticks: {
              fontSize: 11,
              maxRotation: 0,
              padding: 5
            }
          }],
          yAxes: [{
            stacked: true,
            gridLines: {
              display: true,
              color: 'rgba(0, 0, 0, 0.1)',
              drawBorder: false
            },
            ticks: {
              beginAtZero: true,
              fontSize: 11,
              maxTicksLimit: 6,
              padding: 5,
              callback: value => Math.floor(value)
            }
          }]
        },
        legend: {
          display: true,
          position: 'top',
          labels: {
            boxWidth: 12,
            padding: 8,
            fontSize: 11
          }
        },
        tooltips: {
          enabled: false,
          mode: 'point',
          intersect: true,
          position: 'nearest',
          custom: function (tooltipModel) {
            const tooltipEl = document.getElementById(tooltipId)
            if (!tooltipEl) return

            if (tooltipModel.opacity === 0) {
              tooltipEl.style.opacity = 0
              return
            }

            // Posicionamento do tooltip
            const position = this._chart.canvas.getBoundingClientRect()

            // Estilo do tooltip
            tooltipEl.style.opacity = 1
            tooltipEl.style.position = 'absolute'
            tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX + 'px'
            tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY + 'px'
            tooltipEl.style.padding = '8px 12px'
            tooltipEl.style.pointerEvents = 'none'
            tooltipEl.style.backgroundColor = '#ffffff'
            tooltipEl.style.borderRadius = '4px'
            tooltipEl.style.color = '#333333'
            tooltipEl.style.fontSize = '12px'
            tooltipEl.style.transform = 'translate(-50%, -100%)'
            tooltipEl.style.minWidth = '180px'
            tooltipEl.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)'

            // Conteúdo do tooltip
            let innerHtml = '<div style="width: 100%;">'

            if (tooltipModel.body) {
              const date = tooltipModel.title[0]
              const dataPoint = tooltipModel.dataPoints[0]
              const dataset = this._chart.data.datasets[dataPoint.datasetIndex]
              const value = dataPoint.yLabel
              const label = dataset.label
              const imageUrl = dataset.imageUrl
              const backgroundColor = dataset.backgroundColor

              innerHtml += `
                <div style="margin-bottom: 8px; display: flex; align-items: center; gap: 8px;">
                  <span style="width: 10px; height: 10px; border-radius: 2px; flex-shrink: 0; background-color: ${backgroundColor};"></span>
                  <span style="font-size: 2vh; color: #666666;">${date}</span>
                </div>
                <div style="display: flex; align-items: center; gap: 5px;">
                  <img 
                    src="${imageUrl}" 
                    alt="${label}" 
                    style="width: 40px; height: 40px; border-radius: 3px; object-fit: cover; flex-shrink: 0;"
                  />
                  <div style="flex: 1; min-width: 0;">
                    <div style="display: flex; flex-direction: column; gap: 8px;">
                      <span style="font-weight: 600; font-size: 2vh; color: #333333; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; padding-left: 4px;">${label}</span>
                      <span style="font-size: 1.8vh; color: #666666; padding-left: 4px;">respostas: ${value}</span>
                    </div>
                  </div>
                </div>
              `
            }

            innerHtml += '</div>'
            tooltipEl.innerHTML = innerHtml
          }
        }
      }
    },

    handleSubjectSelect (subject) {
      this.selectedSubjectId = subject._id
    }
  }
}
</script>

<style lang="scss" scoped>
.container-stacked-bar {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #fff;
  padding: 0.5vh;
  min-height: 0;
}

.chart-container {
  flex: 1;
  min-height: 0;
  height: calc(100% - 0.5vh);
  position: relative;
  margin-top: 0.5vh;
  display: flex;
  align-items: stretch;
}

canvas {
  width: 100% !important;
  height: 100% !important;
}

:deep(.tooltip-content) {
  .stacked-tooltip-header {
    margin-bottom: 8px;
    .stacked-tooltip-date {
      font-size: 11px;
      color: rgba(255, 255, 255, 0.7);
    }
  }

  .stacked-tooltip-body {
    display: flex;
    flex-direction: column;
  }

  .stacked-tooltip-item {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .stacked-tooltip-info {
    flex: 1;
    min-width: 0;
  }

  .stacked-tooltip-details {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .stacked-tooltip-label-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .stacked-tooltip-color {
    width: 8px;
    height: 8px;
    border-radius: 2px;
    flex-shrink: 0;
  }

  .stacked-tooltip-label {
    font-weight: 500;
    font-size: 13px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .stacked-tooltip-value {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.7);
    margin-left: 16px;
  }
}
</style>
